import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Hey from "@/lib/hey";
import { Trash2, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface DeleteTripProps {
  tripId: string;
  tripName: string;
}

const DeleteTrip = ({ tripId, tripName }: DeleteTripProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && 
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setShowModal(false);
      }
    };

    if (showModal) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showModal]);

  const handleDeleteTrip = async () => {
    try {
      setIsDeleting(true);
      
      console.debug("🗑️ Starting trip deletion process for trip:", tripId);
      const isDeleted = await Hey.HeyUtils.getInstance().removeTrip(tripId);
      
      if (isDeleted) {
        toast({
          title: t("common.success", "Success"),
          description: t("tripCard.tripDeleted", "Trip has been deleted successfully."),
        });
        
        // Immediately navigate to the trips list page (home page)
        // Use replace instead of push to prevent going back to the deleted trip
        navigate("/", { replace: true });
        
        // Force a page refresh to ensure the trip list is updated
        window.location.href = "/";
      } else {
        throw new Error("Failed to delete trip");
      }
    } catch (error) {
      console.error("🚨 ERROR: Error deleting trip:", error);
      
      toast({
        variant: "destructive",
        title: t("common.error", "Error"),
        description: t("tripCard.deleteError", "Failed to delete trip") + ": " + error.message,
      });
    } finally {
      setIsDeleting(false);
      setShowModal(false);
    }
  };

  // Function to handle button click and prevent event propagation
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowModal(true);
    console.debug("Delete button clicked, showing modal");
  };

  return (
    <div className="relative" style={{ zIndex: 1000 }}>
      {/* Delete Button */}
      <Button 
        variant="ghost" 
        size="icon"
        className="text-gray-500 hover:text-black hover:bg-gray-100"
        disabled={isDeleting}
        aria-label={t("tripCard.deleteTrip", "Delete Trip")}
        onClick={handleButtonClick}
        ref={buttonRef}
      >
        {isDeleting ? (
          <span className="h-6 w-6 animate-spin rounded-full border-3 border-current border-t-transparent" />
        ) : (
          <Trash2 className="w-7 h-7 stroke-[2.5px]" />
        )}
      </Button>

      {/* Custom Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1001]" onClick={() => setShowModal(false)}>
          <div 
            ref={modalRef}
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {t("tripCard.confirmDelete", "Delete Trip?")}
              </h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setShowModal(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <p className="text-gray-600 mb-6">
              {t("tripCard.deleteWarning", "This action cannot be undone. This will permanently delete your trip to {{tripName}} and remove it from your account.", { tripName })}
            </p>
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
                className="px-4"
              >
                {t("common.cancel", "Cancel")}
              </Button>
              
              <Button
                variant="destructive"
                onClick={handleDeleteTrip}
                disabled={isDeleting}
                className="px-4"
              >
                {isDeleting ? (
                  <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : null}
                {t("tripCard.confirmDeleteButton", "Yes, delete trip")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteTrip;
